import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, FileText, Trash2, PenLine, BookOpen } from "lucide-react";

export default function Papers() {
  const [items, setItems] = useState([]);
  const [templates, setTemplates] = useState({});
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", journal_template: "IEEE (Standard)", target_journal: "" });
  const nav = useNavigate();

  const load = () => api.get("/papers").then(r => { setItems(r.data); setLoading(false); });
  useEffect(() => { load(); api.get("/papers/templates").then(r => setTemplates(r.data.templates || {})); }, []);

  const create = async () => {
    if (!form.title.trim()) return toast.error("Working title required");
    const { data } = await api.post("/papers", form);
    toast.success("Manuscript created");
    setOpen(false);
    nav(`/app/papers/${data.id}`);
  };
  const del = async (id) => {
    if (!window.confirm("Delete this manuscript?")) return;
    await api.delete(`/papers/${id}`);
    load();
  };

  return (
    <div className="max-w-6xl mx-auto p-8">
      <div className="flex items-start justify-between gap-6 mb-8">
        <div>
          <div className="overline">Publications</div>
          <h1 className="font-display text-5xl mt-2 flex items-center gap-3"><BookOpen className="w-10 h-10 text-primary"/>Research Papers</h1>
          <p className="text-muted-foreground mt-2 max-w-2xl">Write journal-standard manuscripts with sections pre-mapped to Scopus, IEEE, Elsevier, Springer, ACM and Nature templates.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-full" data-testid="btn-new-paper"><Plus className="w-4 h-4 mr-2"/>New manuscript</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-display text-2xl">Start a new manuscript</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div><Label>Working title</Label>
                <Input placeholder="e.g., Explainable AI for Radiology Triage" value={form.title} onChange={(e)=>setForm({...form, title: e.target.value})} data-testid="paper-new-title"/>
              </div>
              <div><Label>Journal template</Label>
                <Select value={form.journal_template} onValueChange={(v)=>setForm({...form, journal_template: v})}>
                  <SelectTrigger data-testid="paper-new-template"><SelectValue/></SelectTrigger>
                  <SelectContent>{Object.keys(templates).map(k => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Target journal (optional)</Label>
                <Input placeholder="e.g., IEEE TPAMI, Lancet…" value={form.target_journal} onChange={(e)=>setForm({...form, target_journal: e.target.value})} data-testid="paper-new-target"/>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={()=>setOpen(false)}>Cancel</Button>
              <Button onClick={create} data-testid="paper-new-create">Create</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? <div className="text-muted-foreground">Loading…</div> :
        items.length === 0 ? (
          <div className="card-crisp p-10 rounded-lg text-center">
            <div className="font-display text-3xl">Your first paper is one click away</div>
            <p className="text-muted-foreground mt-2">Choose a journal template and start writing.</p>
            <Button className="mt-5 rounded-full" onClick={()=>setOpen(true)}><Plus className="w-4 h-4 mr-2"/>Begin</Button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map(p => (
              <div key={p.id} className="card-crisp rounded-lg p-5 flex flex-col group hover:border-primary/60" data-testid={`paper-card-${p.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="w-10 h-10 rounded-md bg-primary/10 text-primary grid place-items-center"><FileText className="w-5 h-5"/></div>
                  <button onClick={()=>del(p.id)} className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive" data-testid={`paper-del-${p.id}`}><Trash2 className="w-4 h-4"/></button>
                </div>
                <div className="font-display text-xl mt-3 leading-tight line-clamp-2">{p.title}</div>
                <div className="text-xs text-muted-foreground mt-2">{p.journal_template}{p.target_journal ? ` · ${p.target_journal}` : ""}</div>
                <div className="text-xs text-muted-foreground mt-1">Updated {new Date(p.updated_at).toLocaleDateString()}</div>
                <div className="mt-4">
                  <Link to={`/app/papers/${p.id}`}>
                    <Button variant="outline" size="sm" className="rounded-full" data-testid={`paper-open-${p.id}`}><PenLine className="w-4 h-4 mr-1"/>Open</Button>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )
      }
    </div>
  );
}
